import {
  Component,
  computed,
  forwardRef,
  HostListener,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ButtonComponent } from '../button/button.component';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
} from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'orbit-date-picker',
  standalone: true,
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  imports: [DatePipe, CdkConnectedOverlay, CdkOverlayOrigin, ButtonComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class DatePickerComponent implements ControlValueAccessor {
  doubleLeftIcon = faAngleDoubleLeft;
  leftIcon = faAngleLeft;
  doubleRightIcon = faAngleDoubleRight;
  rightIcon = faAngleRight;

  minDate = input<Date | null>(null);

  private _value = signal<Date | null>(null);
  value = computed(() => this._value());

  currentDate = signal<Date>(new Date());
  isOpen = signal<boolean>(false);

  // Input parameters for customizable labels with sensible defaults
  todayLabel = input<string>('Today');
  clearLabel = input<string>('Clear');

  // Input parameters for month and weekday names with defaults
  monthNames = input<string[]>([
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]);

  weekdayNames = input<string[]>(['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']);

  months = computed(() => this.monthNames());
  weekdays = computed(() => this.weekdayNames());
  disabled = signal<boolean>(false);

  selectedMonth = computed(() => this.currentDate().getMonth());
  selectedYear = computed(() => this.currentDate().getFullYear());

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.isOpen() && !target.closest('.orbit-date-picker')) {
      this.isOpen.set(false);
    }
  }
  daysInMonth = computed(() => {
    const year = this.selectedYear();
    const month = this.selectedMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: number[] = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(0);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  });

  writeValue(value: Date | null): void {
    this._value.set(value);
    if (value) {
      this.currentDate.set(new Date(value));
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  isBeforeMinDate(day: number): boolean {
    const minDate = this.minDate();
    if (day === 0 || !minDate) return false;

    const date = new Date(this.selectedYear(), this.selectedMonth(), day);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    const compareMinDate = new Date(minDate);
    compareMinDate.setHours(0, 0, 0, 0);

    return compareDate < compareMinDate;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  toggleCalendar(event: MouseEvent): void {
    if (this.disabled()) return;
    this.isOpen.update((open) => !open);
    this.onTouched();
  }

  selectDate(day: number, event?: MouseEvent): void {
    if (day === 0 || this.disabled() || this.isBeforeMinDate(day)) return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const newDate = new Date(this.selectedYear(), this.selectedMonth(), day);

    if (this.isToday(day)) {
      newDate.setHours(23, 59, 59, 999);
    }

    this._value.set(newDate);
    this.onChange(newDate);
    this.onTouched();

    this.isOpen.set(false);
  }

  navigateMonth(direction: number, event: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.currentDate.update((date) => {
      const newDate = new Date(date);
      newDate.setMonth(date.getMonth() + direction);
      return newDate;
    });
  }

  navigateYear(direction: number, event: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.currentDate.update((date) => {
      const newDate = new Date(date);
      newDate.setFullYear(date.getFullYear() + direction);
      return newDate;
    });
  }

  goToToday(event: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const today = new Date();
    this.currentDate.set(today);
    this._value.set(today);
    this.onChange(today);
    this.onTouched();
  }

  clearSelection(event: MouseEvent): void {
    this._value.set(null);

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.onChange(null);
    this.onTouched();
    this.toggleCalendar(event);
  }

  isToday(day: number): boolean {
    if (day === 0) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      this.selectedMonth() === today.getMonth() &&
      this.selectedYear() === today.getFullYear()
    );
  }

  isSelected(day: number): boolean {
    if (!this.value() || day === 0) return false;
    return (
      day === this.value()!.getDate() &&
      this.selectedMonth() === this.value()!.getMonth() &&
      this.selectedYear() === this.value()!.getFullYear()
    );
  }
}
